import React, { useState, useEffect } from 'react';
import axios from 'axios';

interface ReportViewerProps {
  reportId: number;
}

interface ReportData {
  brand_name: string;
  website_url: string;
  analysis_date: string;
  summary: {
    overall_score: number;
    brand_awareness: number;
    service_recognition: number;
    competitive_position: number;
  };
  ai_insights: {
    [key: string]: {
      responses: string[];
      consistency_score: number;
      key_insights: string[];
    };
  };
  recommendations: {
    priority: 'high' | 'medium' | 'low';
    category: string;
    title: string;
    description: string;
    action_items: string[];
  }[];
  competitive_analysis: {
    competitors: string[];
    market_position: string;
    differentiation_opportunities: string[];
  };
}

const ReportViewer: React.FC<ReportViewerProps> = ({ reportId }) => {
  const [report, setReport] = useState<ReportData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [activeTab, setActiveTab] = useState<'summary' | 'insights' | 'recommendations' | 'competitive'>('summary');

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://dashboard.llmseo.fi';

  useEffect(() => {
    const fetchReport = async () => {
      try {
        const response = await axios.get<ReportData>(`${API_BASE_URL}/api/reports/${reportId}`);
        setReport(response.data);
      } catch (err: any) {
        setError(err.response?.data?.error || 'Failed to fetch report');
      } finally {
        setLoading(false);
      }
    };

    fetchReport();
  }, [reportId, API_BASE_URL]);

  const exportToPDF = () => {
    window.open(`${API_BASE_URL}/api/reports/${reportId}/export`, '_blank');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
        <p className="text-red-700">{error}</p>
      </div>
    );
  }

  if (!report) return null;

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getScoreLabel = (score: number) => {
    if (score >= 80) return 'Excellent';
    if (score >= 60) return 'Good';
    if (score >= 40) return 'Fair';
    return 'Needs Improvement';
  };

  return (
    <div className="max-w-6xl mx-auto">
      {/* Report Header */}
      <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              AI Brand Perception Report
            </h1>
            <p className="text-lg text-gray-600">{report.brand_name}</p>
            <p className="text-sm text-gray-500">{report.website_url}</p>
            <p className="text-sm text-gray-500 mt-1">
              Analysis completed: {new Date(report.analysis_date).toLocaleDateString()}
            </p>
          </div>
          <button
            onClick={exportToPDF}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Export PDF
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow p-4">
          <h3 className="text-sm font-medium text-gray-500 mb-1">Overall Score</h3>
          <p className={`text-2xl font-bold ${getScoreColor(report.summary.overall_score)}`}>
            {report.summary.overall_score}/100
          </p>
          <p className="text-sm text-gray-600">{getScoreLabel(report.summary.overall_score)}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <h3 className="text-sm font-medium text-gray-500 mb-1">Brand Awareness</h3>
          <p className={`text-2xl font-bold ${getScoreColor(report.summary.brand_awareness)}`}>
            {report.summary.brand_awareness}/100
          </p>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <h3 className="text-sm font-medium text-gray-500 mb-1">Service Recognition</h3>
          <p className={`text-2xl font-bold ${getScoreColor(report.summary.service_recognition)}`}>
            {report.summary.service_recognition}/100
          </p>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <h3 className="text-sm font-medium text-gray-500 mb-1">Competitive Position</h3>
          <p className={`text-2xl font-bold ${getScoreColor(report.summary.competitive_position)}`}>
            {report.summary.competitive_position}/100
          </p>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="bg-white rounded-lg shadow-lg">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6">
            {[
              { key: 'summary', label: 'Executive Summary' },
              { key: 'insights', label: 'AI Insights' },
              { key: 'recommendations', label: 'Recommendations' },
              { key: 'competitive', label: 'Competitive Analysis' }
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key as any)}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.key
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        <div className="p-6">
          {/* Summary Tab */}
          {activeTab === 'summary' && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold text-gray-900">Executive Summary</h2>
              <div className="prose max-w-none">
                <p className="text-gray-700">
                  Based on comprehensive analysis across Google Gemini, OpenAI GPT, and Anthropic Claude,
                  your brand demonstrates {getScoreLabel(report.summary.overall_score).toLowerCase()} 
                  recognition in the AI landscape.
                </p>
              </div>
            </div>
          )}

          {/* AI Insights Tab */}
          {activeTab === 'insights' && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold text-gray-900">AI Model Insights</h2>
              {Object.entries(report.ai_insights).map(([aiModel, data]) => (
                <div key={aiModel} className="border rounded-lg p-4">
                  <h3 className="font-semibold text-gray-900 mb-2">{aiModel}</h3>
                  <div className="mb-3">
                    <span className="text-sm text-gray-600">Consistency Score: </span>
                    <span className={`font-medium ${getScoreColor(data.consistency_score)}`}>
                      {data.consistency_score}/100
                    </span>
                  </div>
                  <ul className="list-disc list-inside space-y-1">
                    {data.key_insights.map((insight, index) => (
                      <li key={index} className="text-gray-700">{insight}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}

          {/* Recommendations Tab */}
          {activeTab === 'recommendations' && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold text-gray-900">Actionable Recommendations</h2>
              {report.recommendations.map((rec, index) => (
                <div key={index} className="border rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-semibold text-gray-900">{rec.title}</h3>
                    <span className={`px-2 py-1 text-xs font-medium rounded ${
                      rec.priority === 'high' ? 'bg-red-100 text-red-800' :
                      rec.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-green-100 text-green-800'
                    }`}>
                      {rec.priority.toUpperCase()}
                    </span>
                  </div>
                  <p className="text-gray-700 mb-3">{rec.description}</p>
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">Action Items:</h4>
                    <ul className="list-disc list-inside space-y-1">
                      {rec.action_items.map((item, idx) => (
                        <li key={idx} className="text-sm text-gray-600">{item}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Competitive Analysis Tab */}
          {activeTab === 'competitive' && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold text-gray-900">Competitive Landscape</h2>
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Key Competitors</h3>
                <div className="flex flex-wrap gap-2">
                  {report.competitive_analysis.competitors.map((competitor, index) => (
                    <span key={index} className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm">
                      {competitor}
                    </span>
                  ))}
                </div>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Market Position</h3>
                <p className="text-gray-700">{report.competitive_analysis.market_position}</p>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Differentiation Opportunities</h3>
                <ul className="list-disc list-inside space-y-1">
                  {report.competitive_analysis.differentiation_opportunities.map((opp, index) => (
                    <li key={index} className="text-gray-700">{opp}</li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReportViewer;
